import { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router";
import { useAuth } from "@clerk/clerk-react";
import { useAuthSync } from "./hooks/use-auth-sync";
import { useAuthStore } from "./stores/auth.store";
import { CoachLayout } from "./layouts/CoachLayout";
import { UpdatePrompt } from "./components/UpdatePrompt";
import { isAuthV2 } from "@cliently/auth-client";
import { ProtectedRouteV2 } from "./auth-v2/ProtectedRouteV2";

// Pages chargées paresseusement : réduit le JS initial (FullCalendar, TipTap,
// PDF, FormBuilder ne sont téléchargés que lorsqu'on visite la route).
// Les exports sont nommés → on convertit en default dans la closure.
const DashboardPage = lazy(() =>
  import("./pages/pro/DashboardHubPage").then((m) => ({
    default: m.DashboardPage,
  })),
);
const ClientsPage = lazy(() =>
  import("./pages/pro/ClientsPage").then((m) => ({ default: m.ClientsPage })),
);
const ClientDetailPage = lazy(() =>
  import("./pages/pro/ClientDetailPage").then((m) => ({
    default: m.ClientDetailPage,
  })),
);
const NewClientPage = lazy(() =>
  import("./pages/pro/NewClientPage").then((m) => ({ default: m.NewClientPage })),
);
const ImportClientsPage = lazy(() =>
  import("./pages/pro/ImportClientsPage").then((m) => ({
    default: m.ImportClientsPage,
  })),
);
const CalendarPage = lazy(() =>
  import("./pages/pro/CalendarPage").then((m) => ({ default: m.CalendarPage })),
);
const InvoicesPage = lazy(() =>
  import("./pages/pro/InvoicesPage").then((m) => ({ default: m.InvoicesPage })),
);
const SettingsPage = lazy(() =>
  import("./pages/pro/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const MetricGroupsSettingsPage = lazy(() =>
  import("./pages/pro/settings/MetricGroupsSettingsPage").then((m) => ({
    default: m.MetricGroupsSettingsPage,
  })),
);
const ClientLabelsSettingsPage = lazy(() =>
  import("./pages/pro/settings/ClientLabelsSettingsPage").then((m) => ({
    default: m.ClientLabelsSettingsPage,
  })),
);
const AuditLogsPage = lazy(() =>
  import("./pages/pro/AuditLogsPage").then((m) => ({ default: m.AuditLogsPage })),
);
const TeamPage = lazy(() =>
  import("./pages/pro/TeamPage").then((m) => ({ default: m.TeamPage })),
);
const OnboardingPage = lazy(() =>
  import("./pages/pro/OnboardingPage").then((m) => ({
    default: m.OnboardingPage,
  })),
);
const SignInPage = lazy(() =>
  import("./pages/auth/SignInPage").then((m) => ({ default: m.SignInPage })),
);
const SignUpPage = lazy(() =>
  import("./pages/auth/SignUpPage").then((m) => ({ default: m.SignUpPage })),
);
const SignInPageV2 = lazy(() =>
  import("./auth-v2/pages/SignInPageV2").then((m) => ({
    default: m.SignInPageV2,
  })),
);
const SignUpPageV2 = lazy(() =>
  import("./auth-v2/pages/SignUpPageV2").then((m) => ({
    default: m.SignUpPageV2,
  })),
);
const TotpChallengePage = lazy(() =>
  import("./auth-v2/pages/TotpChallengePage").then((m) => ({
    default: m.TotpChallengePage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("./auth-v2/pages/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const VerifyEmailPage = lazy(() =>
  import("./auth-v2/pages/VerifyEmailPage").then((m) => ({
    default: m.VerifyEmailPage,
  })),
);
const TotpEnrollPage = lazy(() =>
  import("./auth-v2/pages/TotpEnrollPage").then((m) => ({
    default: m.TotpEnrollPage,
  })),
);
const BookingPage = lazy(() =>
  import("./pages/BookingPage").then((m) => ({ default: m.BookingPage })),
);
const StorefrontPage = lazy(() =>
  import("./pages/StorefrontPage").then((m) => ({ default: m.StorefrontPage })),
);
const ClientRegisterPage = lazy(() =>
  import("./pages/ClientRegisterPage").then((m) => ({
    default: m.ClientRegisterPage,
  })),
);
const InvitePage = lazy(() =>
  import("./pages/InvitePage").then((m) => ({ default: m.InvitePage })),
);
const NotFoundPage = lazy(() =>
  import("./pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);
const ForbiddenPage = lazy(() =>
  import("./pages/ForbiddenPage").then((m) => ({ default: m.ForbiddenPage })),
);
const HelpPage = lazy(() =>
  import("./pages/pro/HelpPage").then((m) => ({ default: m.HelpPage })),
);
const ChatPage = lazy(() =>
  import("./pages/pro/ChatPage").then((m) => ({ default: m.ChatPage })),
);
const ChatConversationPage = lazy(() =>
  import("./pages/pro/ChatConversationPage").then((m) => ({
    default: m.ChatConversationPage,
  })),
);
const ProgramsPage = lazy(() =>
  import("./pages/pro/ProgramsPage").then((m) => ({ default: m.ProgramsPage })),
);
const ProgramClientsPage = lazy(() =>
  import("./pages/pro/ProgramClientsPage").then((m) => ({
    default: m.ProgramClientsPage,
  })),
);
const ProgramPlannerPage = lazy(() =>
  import("./pages/pro/ProgramPlannerPage").then((m) => ({
    default: m.ProgramPlannerPage,
  })),
);
const StorefrontPreviewPage = lazy(() =>
  import("./pages/pro/StorefrontPreviewPage").then((m) => ({
    default: m.StorefrontPreviewPage,
  })),
);
const ProgramsExercisesPage = lazy(() =>
  import("./pages/pro/ProgramsExercisesPage").then((m) => ({
    default: m.ProgramsExercisesPage,
  })),
);
const FormsPage = lazy(() =>
  import("./pages/pro/forms/FormsPage").then((m) => ({ default: m.FormsPage })),
);
const FormBuilderPage = lazy(() =>
  import("./pages/pro/forms/FormBuilderPage").then((m) => ({
    default: m.FormBuilderPage,
  })),
);
const FormSubmissionsPage = lazy(() =>
  import("./pages/pro/forms/FormSubmissionsPage").then((m) => ({
    default: m.FormSubmissionsPage,
  })),
);
const SiteSharePage = lazy(() =>
  import("./pages/pro/SiteSharePage").then((m) => ({ default: m.SiteSharePage })),
);
const ToolsHubPage = lazy(() =>
  import("./pages/pro/tools/ToolsHubPage").then((m) => ({
    default: m.ToolsHubPage,
  })),
);
const ToolsToolPage = lazy(() =>
  import("./pages/pro/tools/ToolsToolPage").then((m) => ({
    default: m.ToolsToolPage,
  })),
);
const PublicFormFillPage = lazy(() =>
  import("./pages/PublicFormFillPage").then((m) => ({
    default: m.PublicFormFillPage,
  })),
);

const RouteFallback = () => (
  <div
    className="flex h-screen items-center justify-center"
    role="status"
    aria-live="polite"
    aria-label="Chargement de la page"
  >
    <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
  </div>
);

/** Anciennes URLs /pro/… → chemins actuels (favoris, e-mails). */
const LegacyProRedirect = () => {
  const { pathname, search } = useLocation();
  const rest = pathname.replace(/^\/pro/, "");
  let to: string;
  if (rest === "" || rest === "/") to = "/";
  else if (rest.startsWith("/")) to = rest;
  else to = `/${rest}`;
  return <Navigate to={`${to}${search}`} replace />;
};

const FitnessCoachRoute = ({
  children,
}: Readonly<{ children: React.ReactNode }>) => {
  const org = useAuthStore((s) => s.org);
  if (org && org.professionType !== "FITNESS_COACH") {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const ProtectedRouteClerk = ({
  children,
}: Readonly<{ children: React.ReactNode }>) => {
  const { isSignedIn, isLoaded } = useAuth();
  const authReady = useAuthSync();
  const org = useAuthStore((s) => s.org);
  const location = useLocation();

  if (authReady === "client_blocked") {
    return <Navigate to="/forbidden" replace />;
  }

  if (!isLoaded || (isSignedIn && !authReady))
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;

  if (
    org &&
    !org.onboardingCompleted &&
    !location.pathname.startsWith("/onboarding")
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

// Sélection module-level : le flag est stable durant toute la session, on
// évite ainsi d'enfreindre la règle des hooks React.
const ProtectedRoute = isAuthV2() ? ProtectedRouteV2 : ProtectedRouteClerk;

export const App = () => {
  return (
    <>
      <UpdatePrompt />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route
          path="/sign-in/*"
          element={isAuthV2() ? <SignInPageV2 /> : <SignInPage />}
        />
        <Route
          path="/sign-up/*"
          element={isAuthV2() ? <SignUpPageV2 /> : <SignUpPage />}
        />
        {isAuthV2() && (
          <>
            <Route path="/auth/2fa" element={<TotpChallengePage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
            <Route
              path="/settings/2fa"
              element={
                <ProtectedRoute>
                  <TotpEnrollPage />
                </ProtectedRoute>
              }
            />
          </>
        )}
        <Route path="/book/:slug" element={<BookingPage />} />
        <Route path="/p/:slug" element={<StorefrontPage />} />
        <Route path="/register/:slug" element={<ClientRegisterPage />} />
        <Route path="/invite/:token" element={<InvitePage />} />
        <Route path="/f/:token" element={<PublicFormFillPage />} />
        <Route path="/forbidden" element={<ForbiddenPage />} />

        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        <Route path="/pro/*" element={<LegacyProRedirect />} />

        <Route
          element={
            <ProtectedRoute>
              <CoachLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route
            path="/segments"
            element={<Navigate to="/?tab=segments" replace />}
          />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/clients/new" element={<NewClientPage />} />
          <Route path="/clients/import" element={<ImportClientsPage />} />
          <Route path="/clients/:id" element={<ClientDetailPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route
            path="/settings/metrics"
            element={<Navigate to="/settings?tab=metrics" replace />}
          />
          <Route path="/settings/metric-groups" element={<MetricGroupsSettingsPage />} />
          <Route
            path="/settings/client-labels"
            element={<ClientLabelsSettingsPage />}
          />
          <Route path="/site" element={<SiteSharePage />} />
          <Route
            path="/storefront"
            element={<Navigate to="/site" replace />}
          />
          <Route
            path="/sharing"
            element={<Navigate to="/site?tab=sharing" replace />}
          />
          <Route
            path="/services"
            element={<SettingsPage standaloneTab="services" />}
          />
          {/* /promotions reste accessible et renvoie vers l'onglet Promotions du hub Services. */}
          <Route
            path="/promotions"
            element={<Navigate to="/services?tab=promotions" replace />}
          />
          <Route
            path="/form"
            element={<Navigate to="/forms?tab=inscription" replace />}
          />
          <Route path="/forms" element={<FormsPage />} />
          <Route path="/forms/new" element={<FormBuilderPage />} />
          <Route path="/forms/:id/edit" element={<FormBuilderPage />} />
          <Route path="/forms/:id/submissions" element={<FormSubmissionsPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/audit" element={<AuditLogsPage />} />
          <Route path="/tools" element={<ToolsHubPage />} />
          <Route path="/tools/:toolId" element={<ToolsToolPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/requests" element={<Navigate to="/help" replace />} />
          <Route path="/requests/:id" element={<Navigate to="/help" replace />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:conversationId" element={<ChatConversationPage />} />
          <Route path="/programs/clients" element={<ProgramClientsPage />} />
          <Route
            path="/programs/exercises"
            element={
              <FitnessCoachRoute>
                <ProgramsExercisesPage />
              </FitnessCoachRoute>
            }
          />
          <Route
            path="/programs/:id/planner"
            element={<ProgramPlannerPage />}
          />
          <Route path="/programs" element={<ProgramsPage />} />
          <Route
            path="/exercises"
            element={<Navigate to="/programs/exercises" replace />}
          />
          <Route path="/storefront-preview" element={<StorefrontPreviewPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
    </>
  );
};
